import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, amount_total: 0, currency: "usd" });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null });
      return;
    }

    const poll = async () => {
      if (attempts.current >= 8) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      attempts.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setState({ ...data, status: "success" });
          return;
        }
        if (data.status === "expired") {
          setState({ ...data, status: "expired" });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2500);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="container-wrap py-24 min-h-[60vh] flex items-center justify-center">
      <div className="max-w-lg w-full text-center bg-white border border-brand-purple/15 rounded-3xl p-10 shadow-md" data-testid="checkout-success-card">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-brand-purple animate-spin mb-5" />
            <h2 className="font-serif-display text-3xl text-brand-dark mb-2">Confirming your order…</h2>
            <p className="text-brand-dark/70">Hang tight while we verify the payment with Stripe.</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto text-brand-gold mb-5" />
            <h2 className="font-serif-display text-4xl text-brand-dark mb-3" data-testid="checkout-success-title">Thank you!</h2>
            <p className="text-brand-dark/70 mb-2">Your order is confirmed.</p>
            <p className="font-serif-display text-3xl text-brand-purple mb-6">
              ${(state.amount_total / 100).toFixed(2)} <span className="uppercase text-base">{state.currency}</span>
            </p>
            <p className="text-sm text-brand-dark/60 mb-7">Every dollar funds free books and our Young Authors program. ❤</p>
            <Link to="/shop" className="btn-primary" data-testid="back-to-shop-button">
              Back to Shop <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-brand-purple mb-5" />
            <h2 className="font-serif-display text-3xl text-brand-dark mb-2">We couldn't confirm your payment</h2>
            <p className="text-brand-dark/70 mb-6">Please check your email or contact us. If you were charged, we'll make it right.</p>
            <Link to="/shop" className="btn-primary">Back to Shop</Link>
          </>
        )}
      </div>
    </div>
  );
}
